import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
// useCallback se usa en ToastProvider (showToast, success, error, etc.)
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { useReportarProblema } from '../../context/ReportarProblemaContext';
import { agregarSinRepetir } from '../../utils/colaDeToasts';

type ToastType = 'success' | 'error' | 'warning' | 'info';

// Tiempo de permanencia por tipo. error=0 -> no se auto-cierra (el usuario decide cuándo
// cerrarlo o reportarlo). Único punto de verdad: no hardcodear duraciones en los call-sites.
const TOAST_DURATION: Record<ToastType, number> = {
  success: 7000,
  info: 7000,
  warning: 7000,
  error: 0,
};

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
  /** ID del evento ya enviado a Sentry (solo type='error'), para correlacionar con un reporte manual. */
  sentryEventId?: string;
}

/** Opciones del toast de error. `sentryEventId` evita capturar dos veces un error ya reportado. */
interface ErrorToastOptions {
  readonly duration?: number;
  readonly sentryEventId?: string;
}

interface ToastContextType {
  showToast: (message: string, type: ToastType, duration?: number) => void;
  success: (message: string, duration?: number) => void;
  error: (message: string, options?: ErrorToastOptions) => void;
  warning: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

// Referencia global para invocar toasts desde fuera del árbol React (ej: callbacks de Sentry)
let globalToastRef: ToastContextType | undefined;
export const globalToast = {
  success: (msg: string, dur?: number) => { globalToastRef?.success(msg, dur); },
  error: (msg: string, options?: ErrorToastOptions) => { globalToastRef?.error(msg, options); },
  warning: (msg: string, dur?: number) => { globalToastRef?.warning(msg, dur); },
  info: (msg: string, dur?: number) => { globalToastRef?.info(msg, dur); },
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substring(7);
    // La regla de "no repetir el mismo aviso" vive en agregarSinRepetir (utils/colaDeToasts).
    setToasts((prev) => agregarSinRepetir(prev, { id, ...toast }));
  }, []);

  const showToast = useCallback((message: string, type: ToastType, duration?: number) => {
    addToast({ message, type, duration: duration ?? TOAST_DURATION[type] });
  }, [addToast]);

  const success = useCallback((message: string, duration?: number) => {
    showToast(message, 'success', duration);
  }, [showToast]);

  const error = useCallback((message: string, options?: ErrorToastOptions) => {
    // Cada toast de error se reporta a Sentry en este único punto: los ~19 call-sites
    // que hacían catch+toast.error sin capturar nunca llegaban a Sentry (gap real de
    // observabilidad). Si quien llama ya capturó la excepción (reportarErrorInesperado),
    // reusa ese id en vez de generar un segundo evento por el mismo fallo.
    const sentryEventId = options?.sentryEventId ?? Sentry.captureMessage(message, 'error');
    addToast({ message, type: 'error', duration: options?.duration ?? TOAST_DURATION.error, sentryEventId });
  }, [addToast]);

  const warning = useCallback((message: string, duration?: number) => {
    showToast(message, 'warning', duration);
  }, [showToast]);

  const info = useCallback((message: string, duration?: number) => {
    showToast(message, 'info', duration);
  }, [showToast]);

  // Registrar referencia global para uso fuera del árbol React (ej: Sentry callbacks)
  useEffect(() => {
    globalToastRef = { showToast, success, error, warning, info };
    return () => { globalToastRef = undefined; };
  }, [showToast, success, error, warning, info]);

  return (
    <ToastContext.Provider value={{ showToast, success, error, warning, info }}>
      {children}
      {/* data-toast-container: useModalCloseProtection lo excluye del "clic fuera". Sin esto,
          tocar "Agregar un comentario" en un toast de error mientras hay un formulario abierto
          se lee como intento de cerrarlo y dispara "¿Cerrar sin guardar?" encima del reporte. */}
      <div data-toast-container="true" className="fixed bottom-16 sm:bottom-20 right-4 z-[200] space-y-2 max-w-md">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const ToastItem: React.FC<{ toast: Toast; onClose: () => void }> = ({ toast, onClose }) => {
  const { abrir: abrirReporte } = useReportarProblema();
  const [progress, setProgress] = useState(100);
  const [isExiting, setIsExiting] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // Para pausa en hover: acumular tiempo transcurrido
  const elapsedRef = useRef(0);
  const lastTickRef = useRef(Date.now());
  const pausedRef = useRef(false);

  useEffect(() => {
    if (!toast.duration || toast.duration <= 0) return;
    // Limpiar interval previo si existe (React Strict Mode)
    if (intervalRef.current) clearInterval(intervalRef.current);
    elapsedRef.current = 0;
    lastTickRef.current = Date.now();
    intervalRef.current = setInterval(() => {
      if (pausedRef.current) return;
      const now = Date.now();
      elapsedRef.current += now - lastTickRef.current;
      lastTickRef.current = now;
      const remaining = Math.max(0, 100 - (elapsedRef.current / toast.duration!) * 100);
      setProgress(remaining);
      if (remaining <= 0 && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        setIsExiting(true);
        setTimeout(() => onCloseRef.current(), 300);
      }
    }, 50);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [toast.duration]);

  const handleMouseEnter = () => {
    pausedRef.current = true;
  };

  const handleMouseLeave = () => {
    pausedRef.current = false;
    lastTickRef.current = Date.now();
  };

  const handleClose = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsExiting(true);
    setTimeout(() => onClose(), 300);
  };

  const handleReportar = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleClose();
    abrirReporte(toast.sentryEventId);
  };

  const iconMap = {
    success: <CheckCircle className="w-5 h-5" />,
    error: <XCircle className="w-5 h-5" />,
    warning: <AlertCircle className="w-5 h-5" />,
    info: <Info className="w-5 h-5" />
  };

  const colorMap = {
    success: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    error: 'bg-red-50 text-red-800 border-red-200',
    warning: 'bg-yellow-50 text-yellow-800 border-yellow-200',
    info: 'bg-blue-50 text-blue-800 border-blue-200'
  };

  const progressColorMap = {
    success: 'bg-emerald-500',
    error: 'bg-red-500',
    warning: 'bg-yellow-500',
    info: 'bg-blue-500'
  };

  return (
    <div
      onClick={handleClose}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`relative flex flex-col overflow-hidden rounded-lg border-2 shadow-lg cursor-pointer transition-all duration-300 ${
        isExiting ? 'opacity-0 translate-x-8' : 'opacity-100 translate-x-0 animate-in slide-in-from-right'
      } ${colorMap[toast.type]}`}
    >
      <div className="flex items-start gap-3 p-4">
        <div className="flex-shrink-0 mt-0.5">{iconMap[toast.type]}</div>
        <p className="flex-1 text-sm font-medium">{toast.message}</p>
        <button
          onClick={(e) => { e.stopPropagation(); handleClose(); }}
          className="flex-shrink-0 p-1 hover:bg-black/10 rounded transition-colors"
          aria-label="Cerrar notificación"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {toast.type === 'error' && (
        <div className="px-4 pb-3 -mt-1 pl-12 space-y-1">
          <p className="text-xs text-red-700">
            Ya avisamos al equipo. Cuéntanos qué estabas haciendo y lo arreglamos antes.
          </p>
          <button
            type="button"
            onClick={handleReportar}
            className="text-xs font-semibold text-red-800 underline decoration-red-400 underline-offset-2 hover:text-red-900"
          >
            Agregar un comentario
          </button>
        </div>
      )}
      {(toast.duration ?? 0) > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/10">
          <div
            className={`h-full transition-all duration-100 ease-linear ${progressColorMap[toast.type]}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};
